import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatINR, formatDate, previewNumber } from "@/lib/api";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { SearchableSelect } from "@/components/SearchableSelect";
import { DateRangeFilter, inRange } from "@/components/DateRangeFilter";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
// Sprint 2026-Feb-11g — Email panel embedded in expanded row.
import EstimateEmailPanel from "@/components/EstimateEmailPanel";
import { Plus, Trash2, Check, FileCheck2, FileX2, Building2, ChevronDown, ChevronRight, Eye, ClipboardList, X, Search, Package, FileDown } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { useBranch } from "@/contexts/BranchContext";
import { BranchFilter } from "@/components/BranchFilter";
import { useVoucherFormOpen, discardWithConfirm } from "@/lib/voucherGuard";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { useTabs } from "@/contexts/TabsContext";
import { useAuth } from "@/contexts/AuthContext";

const EST_TYPE_PILL = {
  Service:   { color: "bg-sky-100 text-sky-800" },
  Inventory: { color: "bg-emerald-100 text-emerald-800" },
  Asset:     { color: "bg-violet-100 text-violet-800" },
};

export default function Estimates() {
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [createdRange, setCreatedRange] = useState({ from: "", to: "" });
  const [clients, setClients] = useState([]);
  const [expanded, setExpanded] = useState({}); // estimate.id → bool
  const [exporting, setExporting] = useState(null); // {...estimate, _anchor}
  const { active: activeBranch } = useBranch();
  const [filterBranch, setFilterBranch] = useState("");
  const [filterBranchTouched, setFilterBranchTouched] = useState(false);
  const effectiveBranch = filterBranchTouched ? filterBranch : (activeBranch?.id || "");

  // Build a quick lookup so we can resolve the linked shipping-address.code per
  // estimate for the new "Store Code" column.
  const clientsById = useMemo(() => {
    const m = new Map();
    (clients || []).forEach(c => m.set(c.id, c));
    return m;
  }, [clients]);

  useEffect(() => {
    let alive = true;
    api.get("/clients/picker", { params: { scope: "project_access" } }).then(r => { if (alive) setClients(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const resolveStoreCode = (e) => {
    const c = clientsById.get(e.client_id);
    const list = c?.shipping_addresses || [];
    if (!list.length) return "";
    const label = (e.shipping_address_label || "").trim().toLowerCase();
    if (label) {
      const hit = list.find(s => (s.label || "").trim().toLowerCase() === label);
      if (hit?.code) return hit.code;
    }
    const txt = (e.shipping_address || "").toLowerCase();
    if (txt) {
      const hit = list.find(s => (s.address_line1 || "").trim() && txt.includes((s.address_line1 || "").toLowerCase()));
      if (hit?.code) return hit.code;
    }
    return "";
  };

  const params = useMemo(() => {
    const p = {};
    if (effectiveBranch) p.branch_id = effectiveBranch;
    if (filter !== "ALL") p.status = filter;
    if (filterClient) p.client_id = filterClient;
    if (qDebounced) p.q = qDebounced;
    if (createdRange.from) p.date_from = createdRange.from;
    if (createdRange.to) p.date_to = createdRange.to;
    return p;
  }, [effectiveBranch, filter, filterClient, qDebounced, createdRange]);
  // Debounce the search box so we don't hammer the server per keystroke —
  // the server search covers ALL pages (usePaginatedList resets to page 1).
  useEffect(() => {
    const t = setTimeout(() => setQDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/estimates", params);
  const load = reload;
  const openFormTab = useFormTab();
  const { hasPerm } = useAuth();
  // Permission to spawn a new Estimate tab. Hides the "+ New Estimate" button
  // AND suppresses the auto-spawn on landing, so "special" / "viewer" roles
  // see the list only.
  const canCreateEstimate = hasPerm("estimates", "create");

  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap("estimate", pendingIds);

  const openNew = () => openFormTab({
    path: "/estimates/new",
    label: "New Estimate",
    icon: Plus,
    parent: "/estimates",
    onSaved: load,
    // `saved` = refresh parent list but KEEP this tab open so the user can
    // start the next estimate immediately (per Phase 36 / Voucher Tab UX spec).
    render: ({ close, saved, viewList }) => <EstimateModal estimate={null} onClose={close} onSaved={saved} onViewList={viewList} />,
  });
  const openExisting = (e) => openFormTab({
    path: `/estimates/edit/${e.id}`,
    label: `${e.status === "Approved" ? "View" : "Edit"} · ${e.estimate_number || "Estimate"}`,
    icon: Eye,
    parent: "/estimates",
    onSaved: load,
    render: ({ close, saved, viewList }) => <EstimateModal estimate={e} onClose={close} onSaved={saved} onViewList={viewList} />,
  });
  const openJMSPull = (e) => openFormTab({
    path: `/estimates/${e.id}/pull-jms`,
    label: `Pull JMS · ${e.estimate_number || "Estimate"}`,
    icon: ClipboardList,
    parent: "/estimates",
    onSaved: load,
    render: ({ close, saved, viewList }) => <JMSPullModal estimate={e} onClose={close} onSaved={saved} onViewList={viewList} />,
  });

  // Auto-spawn "New Estimate" tab only on the *transition* into /estimates —
  // i.e. the first time the user lands here AND when they switch back from a
  // different module. Once they're on /estimates, closing the form tab is
  // respected (no auto-reopen on the same active path).
  const { activePath, openTabs } = useTabs();
  const prevPathRef = useRef(null);
  useEffect(() => {
    const cameFromElsewhere = prevPathRef.current !== "/estimates";
    prevPathRef.current = activePath;
    if (activePath !== "/estimates") return;
    if (!cameFromElsewhere) return;
    // Skip the auto-spawn entirely for roles without create permission — the
    // "special" persona for example is list-only.
    if (!canCreateEstimate) return;
    const formAlreadyOpen = openTabs.some(
      (t) => t.path === "/estimates/new" || (t.path || "").startsWith("/estimates/edit/")
    );
    if (formAlreadyOpen) return;
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePath, canCreateEstimate]);

  const filtered = list.filter(e => {
    if (filter !== "ALL" && e.status !== filter) return false;
    if (filterClient && e.client_id !== filterClient) return false;
    if (!inRange(e.created_at || e.estimate_date, createdRange)) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    // Estimate-level fields
    const topMatch = [
      e.estimate_number, e.client_name, e.project_name, e.branch_name,
      e.ref_no, e.rc_number, e.gst_number, e.salesman, e.agent, e.status,
      e.notes, e.shipping_address_label,
      String(e.total_amount || ""),
    ].some(v => (v || "").toString().toLowerCase().includes(q));
    if (topMatch) return true;
    // Line-item-level fields (Order Number, Brand/Make, Item Code, Description, etc.)
    return (e.line_items || []).some(li => [
      li.order_number, li.order_month, li.brand, li.warranty, li.client_server,
      li.item_code, li.description, li.serial_no, li.batch_no,
    ].some(v => (v || "").toString().toLowerCase().includes(q)));
  });

  // Auto-expand rows that match via a line-item field so the matching line is visible.
  const lineMatchedIds = new Set(
    !search.trim() ? [] : filtered.filter(e => {
      const q = search.trim().toLowerCase();
      const topMatch = [
        e.estimate_number, e.client_name, e.project_name, e.branch_name,
        e.ref_no, e.rc_number, e.gst_number, e.salesman, e.agent, e.status,
        String(e.total_amount || ""),
      ].some(v => (v || "").toString().toLowerCase().includes(q));
      if (topMatch) return false;
      return (e.line_items || []).some(li => [
        li.order_number, li.order_month, li.brand, li.warranty, li.client_server,
        li.item_code, li.description, li.serial_no, li.batch_no,
      ].some(v => (v || "").toString().toLowerCase().includes(q)));
    }).map(e => e.id)
  );
  const isExpanded = (id) => !!expanded[id] || lineMatchedIds.has(id);
  const toggle = (id) => setExpanded(p => ({ ...p, [id]: !isExpanded(id) }));

  async function approve(id) {
    await api.post(`/estimates/${id}/approve`);
    toast.success("Estimate approved");
    load();
  }
  async function reject(id) {
    const reason = window.prompt("Reason for rejection?") || "";
    await api.post(`/estimates/${id}/reject`, { reason });
    toast.success("Estimate rejected");
    load();
  }
  async function handleDelete(id) {
    if (!window.confirm("Delete estimate?")) return;
    try {
      await api.delete(`/estimates/${id}`);
      toast.success("Deleted"); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  }

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Sales"
        title="Estimates / Quotations"
        subtitle="Create quotations for clients, get them approved, then pull directly into Sales Invoice."
        onRefresh={load}
        refreshing={loading}
        actions={
          canCreateEstimate ? (
            <button data-testid="add-estimate-btn" onClick={openNew} className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New Estimate
            </button>
          ) : null
        }
      />

      <div className="flex gap-1 mb-4 border-b border-slate-200 overflow-x-auto">
        {[["ALL", "All"], ["Draft", "Draft"], ["Sent", "Sent"], ["Approved", "Approved"], ["Partial JMS Pulled", "Partial JMS"], ["Complete JMS pulled", "JMS Pulled"], ["JMS Completed", "JMS Completed"], ["JMS and Invoice Done", "JMS & Invoice Done"], ["Partially Invoiced", "Partially Invoiced"], ["Converted", "Converted"], ["Rejected", "Rejected"]].map(([k, l]) => (
          <button key={k} data-testid={`tab-${k}`} onClick={() => setFilter(k)}
            className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px whitespace-nowrap ${
              filter === k ? "border-[#F97316] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>{l}</button>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="relative flex-1 sm:max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="estimates-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search estimate no, GSTIN, RC, Order No, Brand, Item code/desc, notes…"
            className="w-full pl-9 pr-8 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white"
          />
          {search && (
            <button
              type="button"
              data-testid="estimates-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              title="Clear">
              <X size={13} />
            </button>
          )}
        </div>
        <div className="flex items-center justify-between sm:justify-end gap-3 sm:ml-auto">
          <DateRangeFilter
            value={createdRange}
            onChange={setCreatedRange}
            testidPrefix="estimates"
            label="Created"
          />
          <select
            data-testid="estimates-filter-client"
            value={filterClient}
            onChange={e => setFilterClient(e.target.value)}
            className="text-[11px] uppercase tracking-wider font-bold border border-slate-300 px-2 py-1.5 bg-white text-slate-700 focus:border-[#F97316] focus:outline-none max-w-[200px]"
            title="Filter by Client"
          >
            <option value="">All Clients</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.client_code ? `${c.client_code} · ` : ""}{c.name}</option>
            ))}
          </select>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500" data-testid="estimates-count">
            {total} {filter === "ALL" ? "total" : filter} {search ? `· filtered by "${search}"` : ""}
          </div>
          <BranchFilter value={effectiveBranch} onChange={(v) => { setFilterBranchTouched(true); setFilterBranch(v); }} testid="estimates-filter-branch" />
        </div>
      </div>

      {filtered.length === 0 ? <EmptyState title="No estimates yet" hint="Click 'New Estimate' to create your first quotation." /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="estimates-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  <th className="w-6 py-2 px-1.5"></th>
                  {["Estimate No", "Date", "Branch", "Store Code", "Client POC", "Description", "Total", "Status", "Pending With", "Actions"].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2">{h}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => {
                  const exp = isExpanded(e.id);
                  const lines = e.line_items || [];
                  const storeCode = resolveStoreCode(e);
                  const desc = (e.notes || "").trim();
                  return (
                  <React.Fragment key={e.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50/60 ${exp ? "bg-orange-50/30" : ""}`}>
                    <td className="py-2 px-1.5 align-top">
                      <button
                        data-testid={`expand-est-${e.id}`}
                        onClick={() => toggle(e.id)}
                        title={exp ? "Collapse line items" : "Expand to see line items & details"}
                        className="w-5 h-5 inline-flex items-center justify-center hover:bg-slate-200 text-slate-500 hover:text-[#F97316]"
                        aria-label={exp ? "Collapse" : "Expand"}
                      >
                        {exp ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      </button>
                    </td>
                    <td className="py-2 px-2 text-[11px] font-bold text-slate-900">
                      <button onClick={() => toggle(e.id)} className="text-left hover:text-[#F97316]">{e.estimate_number}</button>
                      <div className="text-[9px] text-slate-400 font-normal mt-0.5">{lines.length} line{lines.length === 1 ? "" : "s"}</div>
                    </td>
                    <td className="py-2 px-2 text-[11px] text-slate-600 whitespace-nowrap">{formatDate(e.estimate_date)}</td>
                    <td className="py-2 px-2 text-[11px]">{e.branch_name ? <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1 py-0.5 font-bold uppercase tracking-wider text-[9px]"><Building2 size={9}/> {e.branch_name}</span> : "—"}</td>
                    <td className="py-2 px-2 text-[11px] tabular font-semibold text-slate-700" data-testid={`store-code-${e.id}`}>{storeCode || <span className="text-slate-300">—</span>}</td>
                    <td className="py-2 px-2 text-[11px] text-slate-700" data-testid={`client-poc-${e.id}`} title={e.agent || ""}>
                      {e.agent ? <span className="block max-w-[140px] truncate">{e.agent}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-slate-700 max-w-[240px]" data-testid={`desc-${e.id}`} title={desc}>
                      {desc ? <span className="line-clamp-2">{desc}</span> : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="py-2 px-2 text-[12px] tabular font-bold whitespace-nowrap">{formatINR(e.total_amount)}</td>
                    <td className="py-2 px-2"><PendingApprovalBadge status={e.status} pendingInfo={pendingMap[e.id]} /></td>
                    <td className="py-2 px-2 text-[11px]" data-testid={`pending-with-${e.id}`}>
                      {e._pending_with && e._pending_with.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[9px] uppercase tracking-wider font-bold text-orange-700">L{e._pending_level} of L{e._pending_final}</span>
                          {e._pending_with.slice(0, 2).map(em => (
                            <span key={em} className="text-[10px] text-slate-700 truncate max-w-[160px]" title={em}>{em}</span>
                          ))}
                          {e._pending_with.length > 2 && (
                            <span className="text-[10px] text-slate-400">+{e._pending_with.length - 2} more</span>
                          )}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="py-2 px-2 text-[11px] whitespace-nowrap">
                      {pendingMap[e.id] && (
                        <span className="mr-2 inline-block align-middle">
                          <TakeActionButton pendingInfo={pendingMap[e.id]} testid={`take-action-est-${e.id}`} />
                        </span>
                      )}
                      {e.status === "Draft" && !pendingMap[e.id] && (
                        <span className="text-[10px] text-slate-400 italic mr-2">Awaits inbox approval</span>
                      )}
                      {(e.previous_invoice_numbers || []).length > 0 && (
                        <span className="block text-[10px] text-slate-400" data-testid={`old-invoices-${e.id}`}>
                          old: <span className="line-through">{(e.previous_invoice_numbers || []).join(", ")}</span>
                        </span>
                      )}
                      {["Converted", "Partially Invoiced", "JMS Completed", "JMS and Invoice Done"].includes(e.status) && e.converted_invoice_number && (
                        <span className="text-xs text-blue-700 mr-3 inline-flex items-center gap-1"><FileCheck2 size={11} />→ {e.converted_invoice_number}</span>
                      )}
                      {e.status !== "Converted" && (
                        <>
                          <button data-testid={`view-est-${e.id}`} onClick={() => openExisting(e)} className="text-slate-500 hover:text-[#F97316] inline-flex items-center gap-1 mr-2" title="View / Edit"><Eye size={14} /></button>
                          {(e.status === "Approved" || e.status === "Partial JMS Pulled" || e.status === "Complete JMS pulled") && hasPerm("jms", "create") && (
                            <button
                              data-testid={`jms-pull-${e.id}`}
                              onClick={() => openJMSPull(e)}
                              title="Mark Ready for JMS — Pull line items into a Joint Measurement Sheet"
                              aria-label="Mark Ready for JMS"
                              className="text-emerald-700 hover:bg-emerald-50 inline-flex items-center justify-center w-7 h-7 mr-2 border border-emerald-200 bg-emerald-50/40"
                            >
                              <ClipboardList size={13} />
                            </button>
                          )}
                          <button
                            data-testid={`export-est-${e.id}`}
                            onClick={(ev) => {
                              const r = ev.currentTarget.getBoundingClientRect();
                              setExporting({ ...e, _anchor: { bottom: window.innerHeight - r.top + 6, right: window.innerWidth - r.right } });
                            }}
                            className="text-slate-500 hover:text-[#F97316] text-xs mr-2 inline-flex items-center gap-1"
                            title="Export PDF"
                          >
                            <FileDown size={12} /> Export
                          </button>
                          {hasPerm("estimates", "delete") && (
                            <button onClick={() => handleDelete(e.id)} className="text-red-600 hover:underline">Delete</button>
                          )}
                        </>
                      )}
                      {e.status === "Converted" && (
                        <>
                          <button data-testid={`view-est-${e.id}`} onClick={() => openExisting(e)} className="text-slate-500 hover:text-[#F97316] inline-flex items-center gap-1 mr-3" title="View"><Eye size={14} /></button>
                          <button
                            data-testid={`export-est-${e.id}`}
                            onClick={(ev) => {
                              const r = ev.currentTarget.getBoundingClientRect();
                              setExporting({ ...e, _anchor: { bottom: window.innerHeight - r.top + 6, right: window.innerWidth - r.right } });
                            }}
                            className="text-slate-500 hover:text-[#F97316] text-xs inline-flex items-center gap-1"
                            title="Export PDF"
                          >
                            <FileDown size={12} /> Export
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {exp && (
                    <tr key={`${e.id}-exp`} data-testid={`lines-${e.id}`} className="bg-orange-50/10 border-b border-slate-200">
                      <td></td>
                      <td colSpan={10} className="px-3 py-3">
                        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                          <Package size={12} className="text-[#F97316]" />
                          Line Items ({lines.length})
                        </div>
                        {lines.length === 0 ? (
                          <div className="text-xs text-slate-400 italic">No line items.</div>
                        ) : (
                          <div className="border border-slate-200 bg-white overflow-x-auto">
                            <table className="w-full text-xs">
                              <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                  {["#", "Item Code", "Description", "Make", "Warranty", "Order No", "Order Month", "Server", "Qty", "Unit", "Rate", "GST%", "Amount"].map(h =>
                                    <th key={h} className="text-left py-2 px-2 text-[9px] uppercase font-bold text-slate-500 whitespace-nowrap">{h}</th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {lines.map((li, i) => (
                                  <tr key={i} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
                                    <td className="py-2 px-2 text-slate-400 tabular">{i + 1}</td>
                                    <td className="py-2 px-2 font-semibold text-slate-700">{li.item_code || "—"}</td>
                                    <td className="py-2 px-2 text-slate-800">{li.description || "—"}</td>
                                    <td className="py-2 px-2 text-slate-700">{li.brand || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-2 text-slate-700">{li.warranty || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-2 text-slate-700 font-semibold">{li.order_number || <span className="text-slate-300 font-normal">—</span>}</td>
                                    <td className="py-2 px-2 text-slate-700">{li.order_month || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-2 text-slate-700">{li.client_server || <span className="text-slate-300">—</span>}</td>
                                    <td className="py-2 px-2 text-right tabular text-slate-800 font-semibold">{li.quantity}</td>
                                    <td className="py-2 px-2 text-slate-600">{li.unit || "—"}</td>
                                    <td className="py-2 px-2 text-right tabular text-slate-700">{formatINR(li.rate)}</td>
                                    <td className="py-2 px-2 text-right tabular text-slate-500">{li.gst_rate ?? 0}%</td>
                                    <td className="py-2 px-2 text-right tabular font-bold text-slate-900">{formatINR(li.amount)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {/* Sprint 2026-Feb-11g — Email status/history panel */}
                        <EstimateEmailPanel estimateId={e.id} statusHint={e.status} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}
      {exporting && (
        <ExportEstimateMenu estimate={exporting} anchor={exporting._anchor} onClose={() => setExporting(null)} />
      )}
    </div>
  );
}

function ExportEstimateMenu({ estimate, anchor, onClose }) {
  async function download(kind) {
    let endpoint, label, ext;
    if (kind === "voucher") { endpoint = "voucher-pdf"; label = "Estimate-Voucher"; ext = "pdf"; }
    else if (kind === "proforma") { endpoint = "proforma-pdf"; label = "Proforma-Invoice"; ext = "pdf"; }
    else if (kind === "compact_job") { endpoint = "compact-job-sheet-pdf"; label = "Compact-Job-Sheet"; ext = "pdf"; }
    else if (kind === "detailed_job") { endpoint = "detailed-job-sheet-pdf"; label = "Detailed-Job-Sheet"; ext = "pdf"; }
    else if (kind === "quantity") { endpoint = "quantity-excel"; label = "Estimate-Quantity"; ext = "xlsx"; }
    else return;
    try {
      const res = await api.get(`/estimates/${estimate.id}/${endpoint}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}-${(estimate.estimate_number || estimate.id).replace(/[/\\]/g, "-")}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${label}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to download");
    }
  }

  const style = anchor
    ? { bottom: `${anchor.bottom}px`, right: `${anchor.right}px`, position: "fixed" }
    : { bottom: "80px", right: "20px", position: "fixed" };

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} data-testid="export-estimate-backdrop" />
      <div className="z-50 bg-white border border-slate-200 shadow-lg w-[290px]"
        style={style} data-testid="export-estimate-menu" role="menu">
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold">Export PDF</div>
          <div className="text-[11px] font-mono font-bold text-[#0B1F3A] truncate">{estimate.estimate_number}</div>
        </div>
        <button data-testid="export-estimate-voucher" onClick={() => download("voucher")}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">Estimate Voucher</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Branch-wise quotation format with full GST breakup.</div>
        </button>
        <button data-testid="export-estimate-proforma" onClick={() => download("proforma")}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">Proforma Invoice (PI Voucher)</div>
          <div className="text-[10px] text-slate-500 mt-0.5">Sales Invoice layout with “PROFORMA INVOICE” heading.</div>
        </button>
        <button data-testid="export-estimate-compact-job" onClick={() => download("compact_job")}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">Compact Job Sheet</div>
          <div className="text-[10px] text-slate-500 mt-0.5">One-page summary for the floor engineer.</div>
        </button>
        <button data-testid="export-estimate-detailed-job" onClick={() => download("detailed_job")}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">Detailed Job Sheet</div>
          <div className="text-[10px] text-slate-500 mt-0.5">5-page bundle: Bill of Delivery + MEPC Approval + Call Service Report + Measurement Sheet + Misc Repair Summary.</div>
        </button>
        <button data-testid="export-estimate-quantity" onClick={() => download("quantity")}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 bg-emerald-50/40">
          <div className="text-xs font-bold text-slate-800">Estimate Quantity Export <span className="text-[9px] uppercase text-emerald-700 font-bold ml-1">XLSX</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">Line items + totals only, in Excel — matches the voucher PDF schema.</div>
        </button>
      </div>
    </>,
    document.body,
  );
}

function JMSPullModal({ estimate, onClose, onSaved, onViewList }) {
  const [existing, setExisting] = useState([]);
  const [pullQty, setPullQty] = useState({}); // idx → qty string
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/jms", { params: { estimate_id: estimate.id } })
      .then(r => setExisting(r.data || []));
  }, [estimate.id]);

  const lines = estimate.line_items || [];
  const pulledByIdx = (idx) => {
    let total = 0;
    for (const j of existing) {
      for (const jl of (j.lines || [])) {
        if (Number(jl.estimate_line_index) === idx) total += Number(jl.quantity || 0);
      }
    }
    return total;
  };
  const remaining = (idx, src) => Math.max(0, Number(src.quantity || 0) - pulledByIdx(idx));

  function setAllRemaining() {
    const next = {};
    lines.forEach((src, i) => { next[i] = String(remaining(i, src)); });
    setPullQty(next);
  }

  async function save() {
    const sel = lines.map((src, i) => ({
      estimate_line_index: i,
      quantity: Number(pullQty[i] || 0),
    })).filter(s => s.quantity > 0);
    if (sel.length === 0) { toast.error("Enter pull qty on at least one line"); return; }
    // Client-side validation
    for (const s of sel) {
      const rem = remaining(s.estimate_line_index, lines[s.estimate_line_index]);
      if (s.quantity > rem + 1e-6) {
        toast.error(`Line ${s.estimate_line_index + 1}: pull ${s.quantity} exceeds remaining ${rem}`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.post("/jms", { estimate_id: estimate.id, lines: sel });
      toast.success("JMS Ready entry created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create JMS");
    } finally { setSaving(false); }
  }

  const totalRemaining = lines.reduce((s, src, i) => s + remaining(i, src), 0);

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="jms-pull-modal">
      <div className="bg-white min-h-full">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-5 border-b border-slate-200 flex flex-col md:flex-row md:items-center md:justify-between gap-3 sticky top-0 bg-white z-10 shadow-sm">
          <div className="flex items-start gap-3 min-w-0">
            <ClipboardList size={22} className="text-emerald-700 shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h2 className="font-display text-lg sm:text-2xl font-bold">Mark Ready for JMS — {estimate.estimate_number}</h2>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {estimate.client_name} · {estimate.project_name} · {estimate.branch_name || "—"} · Pull partial or full quantity per line. You can pull multiple times until each line hits 0 remaining.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="jms-pull-all" onClick={setAllRemaining}
              className="text-[10px] font-bold uppercase tracking-wider border border-slate-300 px-3 py-2 hover:border-[#F97316] hover:text-[#F97316]"
              disabled={totalRemaining <= 0}>
              Pull All Remaining
            </button>
            <button type="button" onClick={onClose}
              className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1">
              <X size={14} /> Close
            </button>
          </div>
        </div>
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 max-w-[1400px] mx-auto">
          {existing.length > 0 && (
            <div className="mb-4 text-[11px] text-slate-700 bg-orange-50 border border-orange-200 px-3 py-2">
              <b>{existing.length}</b> JMS record(s) already exist for this estimate. Remaining qty is calculated after deducting prior pulls.
            </div>
          )}
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-10">#</th>
                  <th className="text-left py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500">Description</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-24">Est Qty</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-24">Pulled</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-24">Remaining</th>
                  <th className="text-right py-2.5 px-3 text-[10px] uppercase font-bold text-slate-500 w-32">Pull Qty</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((src, i) => {
                  const rem = remaining(i, src);
                  const pulled = pulledByIdx(i);
                  const disabled = rem <= 0;
                  return (
                    <tr key={i} className={`border-b border-slate-100 ${disabled ? "opacity-60" : ""}`}>
                      <td className="py-2.5 px-3 text-slate-500">{i + 1}</td>
                      <td className="py-2.5 px-3">
                        <div className="font-semibold text-slate-800">{src.item_code ? `${src.item_code} · ` : ""}{src.description}</div>
                        <div className="text-[10px] text-slate-500">{src.unit} · {formatINR(src.rate)} / unit{src.brand ? ` · ${src.brand}` : ""}</div>
                      </td>
                      <td className="py-2.5 px-3 text-right tabular">{src.quantity}</td>
                      <td className="py-2.5 px-3 text-right tabular text-orange-700 font-semibold">{pulled}</td>
                      <td className="py-2.5 px-3 text-right tabular font-bold text-emerald-700">{rem}</td>
                      <td className="py-2.5 px-3">
                        <input
                          data-testid={`jms-pull-qty-${i}`}
                          type="number" step="0.01" min="0" max={rem}
                          value={pullQty[i] ?? ""}
                          disabled={disabled}
                          onChange={e => setPullQty({ ...pullQty, [i]: e.target.value })}
                          placeholder={disabled ? "Done" : "0"}
                          className="w-full px-2 py-1.5 border border-slate-300 text-right text-xs focus:border-[#F97316] focus:outline-none disabled:bg-slate-100"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalRemaining <= 0 && (
            <div className="mt-4 text-center text-xs text-slate-500 bg-slate-50 border border-slate-200 py-4">
              All quantities already pulled into JMS records. Visit the JMS page to mark them Completed.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-6 border-t border-slate-200 mt-6">
            <button onClick={onClose} className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider border border-slate-300 hover:bg-slate-50">Cancel</button>
            <button
              data-testid="save-jms-pull"
              onClick={save}
              disabled={saving || totalRemaining <= 0}
              className="px-5 py-2.5 text-xs font-bold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create JMS Ready"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EstimateModal({ estimate, onClose, onSaved, onViewList }) {
  const navigate = useNavigate();
  useVoucherFormOpen();
  const mastersRefreshKey = useMastersRefresh();
  // Discard exits to the application dashboard (not the list).
  const handleDiscard = () => discardWithConfirm(() => navigate("/"), estimate ? "this estimate edit" : "this new estimate");
  const { active: activeBranch, branches } = useBranch();
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState([]);
  // Memoised "fresh draft" used both for the initial mount AND when resetting
  // the form for the next entry after a successful CREATE (so the user can
  // immediately start a new estimate in the same tab — Phase 36 spec).
  const blankDraft = () => ({
    estimate_number: "",
    estimate_date: new Date().toISOString().slice(0, 10),
    branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || "",
    client_id: "", project_id: "",
    valid_until: "",
    sales_gst_type: "Tax Invoice",
    document_type_credit: "Credit",
    location: "", category: "", warehouse: "", email: "",
    currency: "INR", conversion_rate: 1,
    salesman: "", service_period: "", agent: "", agent_secondary: "",
    tax_treatment: "Exclusive",
    payment_terms: "30 Days",
    ref_no: "", ref_date: "", rc_number: "",
    mail_approval_date: "", work_completion_date: "",
    billing_address: "", shipping_address: "", gst_number: "",
    line_items: [{ item_id: "", item_code: "", description: "", serial_no: "", batch_no: "", pack_unit: "Box", pack_qty: 1, quantity: 0, unit: "Nos", base_rate: 0, rate: 0, gst_rate: 18, amount: 0 }],
    tds_amount: 0,
    enable_round_up: true,
    terms_conditions: "", notes: "",
  });
  const [form, setForm] = useState(estimate || blankDraft());
  const [saving, setSaving] = useState(false);
  // Mirror of InvoicePage's `justSaved` — keeps the Save button disabled for
  // ~600 ms after a successful submit so a double-click can't immediately
  // re-fire the now-blank form.
  const [justSaved, setJustSaved] = useState(false);
  // Jul-2026 — Save & Send confirmation popup state:
  // { to: [{source,label,name,email,removed}], cc: [{email,removed}], email_enabled }
  const [sendModal, setSendModal] = useState(null);
  const [groups, setGroups] = useState([]);
  const [groupFilter, setGroupFilter] = useState("");
  // Aug-2026 (user report) — datalist ids MUST be unique per form instance:
  // the tab system keeps "New Estimate" and "Edit" forms mounted together,
  // and duplicate ids made edit-form inputs bind to the New tab's UNFILTERED
  // datalist, so the Group filter looked dead in edit mode.
  const dlUid = useMemo(() => Math.random().toString(36).slice(2, 8), []);
  const [company, setCompany] = useState(null);
  const set = (k, v) => setForm({ ...form, [k]: v });

  useEffect(() => {
    const branchParam = form.branch_id ? { params: { branch_id: form.branch_id } } : {};
    Promise.all([
      api.get("/clients", branchParam), api.get("/projects-picker", branchParam), api.get("/items/_pickable"),
      api.get("/item-groups"), api.get("/members/_pickable", branchParam), api.get("/categories"), api.get("/company-settings"),
    ])
      .then(([c, p, it, g, m, cat, co]) => { setClients(c.data); setProjects(p.data); setItems(it.data); setGroups(g.data || []); setMembers(m.data || []); setCategories(Array.isArray(cat.data) ? cat.data : []); setCompany(co.data); });
    if (!estimate) {
      previewNumber("estimate", new Date().toISOString().slice(0, 10)).then(n => {
        if (n) setForm(f => ({ ...f, estimate_number: f.estimate_number || n }));
      });
    }
  }, [estimate, form.branch_id, mastersRefreshKey]);

  // An item belongs to a group if its `group_id` matches the group's id,
  // OR its `group_name` matches the group's name case-insensitively. The
  // latter catches items added via Excel bulk upload or saved before they
  // were linked to the Item Group library (legacy rows where only the
  // free-text label was stored).
  function _itemInGroup(it, g) {
    if (!g) return false;
    if ((it.group_id || "") && it.group_id === g.id) return true;
    const inm = (it.group_name || "").trim().toLowerCase();
    const gnm = (g.name || "").trim().toLowerCase();
    return !!inm && inm === gnm;
  }
  const filteredItems = groupFilter
    ? items.filter(it => _itemInGroup(it, groups.find(g => g.id === groupFilter)))
    : items;

  // Derive picker text for a line: explicit user input wins; else fall back
  // to canonical "name · ItemType" computed from item_id (edit mode).
  function pickerLabelFor(l) {
    if (l._picker_label !== undefined) return l._picker_label;
    if (!l.item_id) return "";
    const it = items.find(x => x.id === l.item_id);
    if (!it) return "";
    return `${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`;
  }

  function setLine(idx, k, v) {
    const lines = [...form.line_items];
    lines[idx] = { ...lines[idx], [k]: v };
    const ln = lines[idx];
    if (k === "base_rate") {
      // User edited Unit Amount → recompute management fee + effective rate
      const base = Number(ln.base_rate || 0);
      const mode = ln.management_fee_type || "none";
      const val = Number(ln.management_fee_value || 0);
      const fee = mode === "percent" ? (base * val) / 100 : mode === "amount" ? val : 0;
      ln.management_fee_amount = Number(fee.toFixed(2));
      ln.rate = Number((base + fee).toFixed(2));
    }
    if (k === "quantity" || k === "rate" || k === "base_rate") {
      ln.amount = Number(Number(ln.quantity || 0) * Number(ln.rate || 0));
    }
    lines[idx] = ln;
    setForm({ ...form, line_items: lines });
  }
  function pickItem(idx, itemId) {
    const it = items.find(x => x.id === itemId);
    const lines = [...form.line_items];
    if (it) {
      // Estimate = sales-side → apply Management Fee
      const base = Number(it.sales_rate ?? it.rate ?? 0);
      const mode = it.management_fee_type || "none";
      const val = Number(it.management_fee_value || 0);
      const fee = mode === "percent" ? (base * val) / 100 : mode === "amount" ? val : 0;
      const effRate = base + fee;
      const qty = Number(lines[idx].quantity || 0);
      lines[idx] = {
        ...lines[idx],
        item_id: it.id, item_code: it.item_code, description: it.name,
        unit: it.unit || "NOS", rate: Number(effRate.toFixed(2)), gst_rate: Number(it.gst_rate || 18),
        amount: Number((qty * effRate).toFixed(2)),
        item_type: it.item_type || "Inventory",
        rc_line_item_no: it.rc_line_item_no || "",
        arc_number: it.arc_number || "",
        type: it.type || "",
        brand: it.brand || "",
        warranty: it.warranty || "",
        order_number: it.order_number || "",
        order_month: it.order_month || "",
        client_server: it.client_server || "",
        _picker_label: `${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`,
        base_rate: base,
        management_fee_type: mode,
        management_fee_value: val,
        management_fee_amount: Number(fee.toFixed(2)),
      };
    } else {
      lines[idx] = { ...lines[idx], item_id: "", item_code: "", _picker_label: "" };
    }
    setForm({ ...form, line_items: lines });
  }
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, { item_id: "", item_code: "", description: "", serial_no: "", batch_no: "", pack_unit: "Box", pack_qty: 1, quantity: 0, unit: "Nos", base_rate: 0, rate: 0, gst_rate: 18, amount: 0 }] });
  const removeLine = (i) => setForm({ ...form, line_items: form.line_items.filter((_, idx) => idx !== i) });

  const isTaxable = (form.sales_gst_type || "Tax Invoice") === "Tax Invoice";
  const subtotal = form.line_items.reduce((s, l) => s + Number(l.amount || 0), 0);
  const tax = isTaxable ? form.line_items.reduce((s, l) => s + Number(l.amount || 0) * Number(l.gst_rate || 0) / 100, 0) : 0;

  // GST bifurcation: intra-state → CGST + SGST; inter-state → IGST.
  // Seller state resolution chain: Branch.state_code → Branch.gst_number[:2]
  // → CompanySettings.state_code → CompanySettings.gstin[:2]. This handles
  // the case where Head Office (default branch) has no GSTIN configured yet
  // but the company-level GSTIN is present.
  const selectedBranch = branches.find(b => b.id === form.branch_id);
  const sellerState =
    ((selectedBranch?.state_code || "").slice(0, 2)) ||
    ((selectedBranch?.gst_number || "").slice(0, 2)) ||
    ((company?.state_code || "").slice(0, 2)) ||
    ((company?.gstin || "").slice(0, 2));
  const buyerState = (form.gst_number || "").slice(0, 2);
  const isIntraState = isTaxable && !!sellerState && !!buyerState && sellerState === buyerState;
  // Section 8, CGST Act: CGST MUST equal SGST on intra-state supplies.
  // Compute the half ONCE with round-half-up (matches the backend's
  // `_apply_totals` Decimal-based split). See InvoicePage.jsx for the
  // detailed bug-class comment — same fix repeated here so the Estimate
  // edit screen never shows an asymmetric split.
  const roundHalfUp2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
  const half = isIntraState ? roundHalfUp2(tax / 2) : 0;
  const cgst = half;
  const sgst = half;
  const taxDisplay = isIntraState ? roundHalfUp2(half * 2) : roundHalfUp2(tax);
  const igst = isTaxable && !isIntraState ? roundHalfUp2(tax) : 0;
  const rawTotal = subtotal + taxDisplay;
  const total = form.enable_round_up ? Math.round(rawTotal) : rawTotal;
  const roundAdj = form.enable_round_up ? +(Math.round(rawTotal) - rawTotal).toFixed(2) : 0;
  const net = total;

  async function save(e) {
    e.preventDefault();
    if (!form.project_id || !form.client_id) { toast.error("Project and Client are required"); return; }
    if (!form.branch_id) { toast.error("Branch is required"); return; }
    // Jul-2026 — Save & Send flow: NEW estimates go through the recipient
    // confirmation popup before saving. Edits keep the classic direct save.
    if (!estimate) {
      try {
        const { data } = await api.post("/estimates/recipients-preview", {
          client_id: form.client_id,
          agent: form.agent || "",
          agent_secondary: form.agent_secondary || "",
          shipping_address_label: form.shipping_address_label || "",
        });
        setSendModal({
          to: (data.to || []).map(r => ({ ...r, removed: false })),
          cc: (data.cc || []).map(c => ({ email: c, removed: false })),
          email_enabled: !!data.email_enabled,
        });
      } catch {
        setSendModal({ to: [], cc: [], email_enabled: false });
      }
      return;
    }
    await doSave(null);
  }

  async function doSave(emailMode) {
    setSaving(true);
    const client = clients.find(c => c.id === form.client_id);
    const project = projects.find(p => p.id === form.project_id);
    const payload = {
      ...form,
      client_name: client?.name || "",
      project_name: project?.project_name || "",
      gst_number: form.gst_number || client?.gst_number || "",
      tds_amount: 0,
      conversion_rate: Number(form.conversion_rate || 1),
      line_items: form.line_items.map(l => {
        const { _picker_label, _expanded, ...rest } = l;
        return {
          ...rest, quantity: Number(l.quantity || 0), rate: Number(l.rate || 0),
          gst_rate: Number(l.gst_rate || 0), amount: Number(l.amount || 0),
          pack_qty: Number(l.pack_qty || 1),
        };
      }),
    };
    try {
      let res;
      if (estimate) {
        res = await api.put(`/estimates/${estimate.id}`, payload);
        if (res.data?._re_approval_required) {
          const ar = res.data?._approval_request;
          const rejected = res.data?._resubmitted_after_rejection;
          if (ar?.status === "Approved") {
            toast.success(rejected ? "Rejected estimate resubmitted & auto-approved (you are at top level)" : "Estimate updated & auto-approved (you are at top level)");
          } else if (ar) {
            toast.warning(rejected
              ? `Rejected estimate resubmitted for approval — pending at L${ar.current_level} of L${ar.final_level}`
              : `Material change detected — re-submitted for approval, pending at L${ar.current_level} of L${ar.final_level}`);
          } else {
            toast.warning(rejected ? "Rejected estimate reverted to Draft (configure Approval Matrix to enable workflow)" : "Material change detected — reverted to Draft (configure Approval Matrix to enable workflow)");
          }
        } else {
          toast.success("Estimate updated");
        }
      } else {
        res = await api.post(`/estimates${emailMode ? `?email_mode=${emailMode}` : ""}`, payload);
        const ar = res.data?._approval_request;
        if (ar?.status === "Approved") {
          toast.success("Estimate created & auto-approved — start the next one below");
        } else if (ar) {
          toast.success(`Estimate created (pending at L${ar.current_level} of L${ar.final_level}) — start the next one below`);
        } else {
          toast.success("Estimate created as Draft — start the next one below");
        }
      }
      // Refresh the parent list silently — DO NOT close the tab.
      onSaved();
      // For a *new* estimate, reset the form to a blank draft so the user can
      // immediately start the next entry in the same tab. (For an edit we
      // leave the form mounted with the updated values so the user can see
      // their change land — they'll close the tab via the X button.)
      if (!estimate) {
        setForm(blankDraft());
        // Reset scroll to the top so the new blank form is immediately visible.
        try {
          window.scrollTo({ top: 0, behavior: "smooth" });
        } catch { /* old browsers */ }
      }
      // Brief lock to swallow accidental double-clicks on the orange Save
      // button (eliminates the "saves twice in a row" rebound).
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 600);
      return res?.data || null;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
      return null;
    } finally { setSaving(false); }
  }

  // Jul-2026 — Save & Send confirmations
  async function confirmSaveAndSend() {
    const to = sendModal.to.filter(r => r.email && !r.removed && !r.movedToCc).map(r => r.email);
    const cc = sendModal.cc.filter(c => !c.removed).map(c => c.email);
    setSendModal(null);
    const doc = await doSave("confirm");
    if (!doc?.id) return;
    if (to.length === 0) {
      toast.info("Saved — all email recipients were removed, so no email was sent.");
      return;
    }
    try {
      const { data: log } = await api.post(`/estimates/${doc.id}/send-initial-confirmed`, { to, cc });
      if (log.status === "sent") toast.success(`Estimate emailed to ${to.join(", ")}`);
      else if (log.status === "skipped") toast.warning(`Email skipped: ${log.failure_reason || "see Email History"}`);
      else toast.error(`Email failed: ${log.failure_reason || "see Email History"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email send failed (estimate was saved)");
    }
  }

  async function saveWithoutEmail() {
    setSendModal(null);
    await doSave("skip");
  }

  const onClientChange = (id) => {
    const c = clients.find(x => x.id === id);
    const ship = c?.shipping_addresses?.length
      ? (c.shipping_addresses.find(s => s.is_default) || c.shipping_addresses[0])
      : null;
    const shipStr = ship ? [ship.label, ship.address_line1, ship.address_line2, [ship.city, ship.state, ship.pincode].filter(Boolean).join(", ")].filter(Boolean).join("\n") : "";
    setForm({
      ...form, client_id: id,
      email: c?.email || form.email,
      billing_address: c?.billing_address || c?.address || form.billing_address,
      shipping_address: shipStr || c?.address || form.shipping_address,
      shipping_address_label: ship ? ship.label : "",
      gst_number: c?.gst_number || form.gst_number,
    });
  };

  const activeClient = clients.find(c => c.id === form.client_id);
  const estShipOptions = activeClient?.shipping_addresses || [];
  function pickEstShip(idx) {
    const s = estShipOptions[idx];
    if (!s) return;
    const text = [s.address_line1, s.address_line2, [s.city, s.state, s.pincode].filter(Boolean).join(", "),
                  s.contact_person ? `Contact: ${s.contact_person} ${s.contact_phone || ""}`.trim() : ""].filter(Boolean).join("\n");
    setForm({ ...form, shipping_address: text, shipping_address_label: s.label || "" });
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="estimate-form-overlay">
      <div className="bg-white min-h-full">
        <div className="px-4 sm:px-6 lg:px-8 py-3 sm:py-5 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sticky top-0 bg-white z-10 shadow-sm">
          <h2 className="font-display text-xl sm:text-2xl font-bold">{estimate ? "Edit Estimate" : "New Estimate / Quotation"}</h2>
          <div className="flex gap-2 self-start sm:self-auto">
            <button type="button" data-testid="view-estimate-list-btn" onClick={onViewList || onClose}
              className="bg-white border border-slate-300 px-4 py-2 sm:py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
            <RefreshMastersButton testid="estimate-refresh-masters-btn" />
          </div>
        </div>
        <form onSubmit={save} className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6 space-y-6 max-w-[1600px] mx-auto">

          {/* Top header — 3-column compact AlignBooks style */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-1 pb-4 border-b border-slate-200">
            {/* COLUMN 1 — Classification & Customer */}
            <div className="space-y-1">
              <ECompactField label="Category">
                <input
                  data-testid="est-category"
                  list="est-category-list"
                  value={form.category}
                  onChange={e => set("category", e.target.value)}
                  placeholder="Pick or type new (e.g. Lakme)"
                  className={eCompact}
                />
                <datalist id="est-category-list">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
              </ECompactField>
              <ECompactField label="GST Type">
                <select data-testid="est-gst-type" value={form.sales_gst_type} onChange={e => set("sales_gst_type", e.target.value)} className={eCompact}>
                  {["Tax Invoice", "Bill of Supply - Non GST"].map(s => <option key={s}>{s}</option>)}
                </select>
              </ECompactField>
              <ECompactField label="Branch *" required>
                <select data-testid="est-branch" required value={form.branch_id || ""} onChange={e => {
                  const b = branches.find(x => x.id === e.target.value);
                  setForm({ ...form, branch_id: e.target.value, branch_name: b?.branch_name || "" });
                }} className={eCompact}>
                  <option value="">— Select Branch —</option>
                  {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}{b.gst_number ? ` (${b.gst_number})` : ""}</option>)}
                </select>
              </ECompactField>
              <ECompactField label="Customer *" required>
                <SearchableSelect
                  required
                  value={form.client_id}
                  onChange={(id) => onClientChange(id)}
                  items={clients}
                  getKey={(c) => c.id}
                  getLabel={(c) => `${c.client_code || ""} · ${c.name || ""}`}
                  getSubLabel={(c) => c.gst_number || c.contact_person || ""}
                  placeholder="Select customer…"
                  testid="est-client-picker"
                />
              </ECompactField>
              <ECompactField label="Email"><input type="email" value={form.email} onChange={e => set("email", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Salesman">
                <input list="estimate-members-list" value={form.salesman} onChange={e => set("salesman", e.target.value)}
                  placeholder="Type to search…" data-testid="est-salesman" className={eCompact} />
                <datalist id="estimate-members-list">
                  {members.map(m => <option key={m.id} value={m.name}>{m.designation ? `${m.designation} · ` : ""}{m.member_code || ""}</option>)}
                </datalist>
              </ECompactField>
              <ECompactField label="Service Period"><input data-testid="est-service-period" value={form.service_period || ""} onChange={e => set("service_period", e.target.value)} placeholder="e.g. Jan–Mar 2026" className={eCompact} /></ECompactField>
            </div>

            {/* COLUMN 2 — Document numbering & dates */}
            <div className="space-y-1">
              <ECompactField label="Date *" required><input type="date" required value={form.estimate_date} onChange={e => set("estimate_date", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Estimate No *" required>
                <VoucherNumberInput
                  module="estimate"
                  value={form.estimate_number}
                  onChange={v => set("estimate_number", v)}
                  testidPrefix="est-number"
                />
              </ECompactField>
              <ECompactField label="Project *" required>
                <SearchableSelect
                  required
                  value={form.project_id}
                  onChange={(id) => set("project_id", id)}
                  items={projects}
                  getKey={(p) => p.id}
                  getLabel={(p) => `${p.project_code || ""} · ${p.project_name || ""}`}
                  getSubLabel={(p) => p.client_name || p.branch_name || ""}
                  placeholder="Select project…"
                  testid="est-project-picker"
                />
              </ECompactField>
              <ECompactField label="Valid Until"><input type="date" value={form.valid_until} onChange={e => set("valid_until", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Ref No"><input value={form.ref_no} onChange={e => set("ref_no", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="RC Number"><input data-testid="est-rc-number" value={form.rc_number || ""} onChange={e => set("rc_number", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Ref Date"><input type="date" value={form.ref_date} onChange={e => set("ref_date", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Mail Approval Date"><input data-testid="est-mail-approval-date" type="date" value={form.mail_approval_date || ""} onChange={e => set("mail_approval_date", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Work Completion Date"><input data-testid="est-work-completion-date" type="date" value={form.work_completion_date || ""} onChange={e => set("work_completion_date", e.target.value)} className={eCompact} /></ECompactField>
              <ECompactField label="Tax">
                <select value={form.tax_treatment} onChange={e => set("tax_treatment", e.target.value)} className={eCompact}>
                  {["Exclusive", "Inclusive"].map(s => <option key={s}>{s}</option>)}
                </select>
              </ECompactField>
              <ECompactField label="GST Number">
                <input data-testid="est-gst-number" value={form.gst_number} onChange={e => set("gst_number", e.target.value)} className={eCompact} />
                {isTaxable && buyerState && (
                  <div className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${isIntraState ? "text-emerald-700" : "text-orange-700"}`}>
                    {sellerState ? `Seller ${sellerState} · Buyer ${buyerState} → ${isIntraState ? "Intra-state (CGST + SGST)" : "Inter-state (IGST)"}` : "Set branch / company GST to detect place of supply"}
                  </div>
                )}
              </ECompactField>
              <ECompactField label="Currency">
                <select value={form.currency} onChange={e => set("currency", e.target.value)} className={eCompact}>
                  {["INR", "USD", "EUR", "GBP", "AED"].map(c => <option key={c}>{c}</option>)}
                </select>
              </ECompactField>
            </div>

            {/* COLUMN 3 — Bill To / Ship To */}
            <div className="space-y-3">
              <div className="border border-slate-200">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600">📄 Bill To</div>
                <textarea rows={3} value={form.billing_address} onChange={e => set("billing_address", e.target.value)} className="w-full px-3 py-2 text-xs border-0 focus:outline-none resize-none" />
              </div>
              <div className="border border-slate-200">
                <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600 flex items-center justify-between gap-2">
                  <span>📦 Ship To {form.shipping_address_label ? <span className="text-[#F97316] normal-case ml-1">· {form.shipping_address_label}</span> : null}</span>
                  {(form.billing_address || "").trim() && (
                    <button type="button" data-testid="est-ship-same-as-billing"
                      onClick={() => setForm(f => ({ ...f, shipping_address: f.billing_address, shipping_address_label: "Same as Billing" }))}
                      className="text-[10px] font-bold uppercase tracking-wider text-[#F97316] hover:underline">
                      Same as Billing
                    </button>
                  )}
                </div>
                {activeClient && (
                  <div className="px-3 py-2 bg-blue-50/40 border-b border-slate-200">
                    {estShipOptions.length > 0 ? (
                      <SearchableSelect
                        value={form.shipping_address_label || ""}
                        onChange={(_id, row) => {
                          const idx = estShipOptions.findIndex((s) => (s.label || "") === (row?.label || ""));
                          if (idx >= 0) pickEstShip(idx);
                        }}
                        items={estShipOptions}
                        getKey={(s) => s.label || `${s.address_line1 || ""}`}
                        getLabel={(s) => s.label || `Addr · ${s.city || ""}`}
                        getSubLabel={(s) => {
                          const parts = [s.code, s.format, s.city, s.state].filter(Boolean);
                          return parts.join(" · ");
                        }}
                        getSearchText={(s) =>
                          [s.label, s.code, s.format, s.city, s.state, s.pincode, s.address_line1, s.address_line2, s.contact_person]
                            .filter(Boolean).join(" ")
                        }
                        placeholder={`Pick from ${estShipOptions.length} saved address${estShipOptions.length === 1 ? "" : "es"}…`}
                        searchPlaceholder="Search label / code / city / address…"
                        testid="est-ship-picker"
                      />
                    ) : (
                      <p className="text-[10px] text-slate-500 italic">No saved shipping addresses for this client — edit shipping address directly below, or add labelled addresses in the Client master.</p>
                    )}
                  </div>
                )}
                <textarea rows={3} value={form.shipping_address} onChange={e => set("shipping_address", e.target.value)} className="w-full px-3 py-2 text-xs border-0 focus:outline-none resize-none" placeholder="Shipping Address — start typing in the search box above to pick saved" />
              </div>
              {(() => {
                const sel = clients.find(c => c.id === form.client_id);
                const pocs = sel?.pocs || [];
                return (
                  <>
                    <ECompactField label="Client POC">
                      <input list="estimate-client-pocs" value={form.agent} onChange={e => set("agent", e.target.value)}
                        placeholder={pocs.length ? "Search POC…" : "Pick a customer first"}
                        disabled={!sel} data-testid="est-client-poc" className={eCompact} />
                      <datalist id="estimate-client-pocs">
                        {pocs.map((p, i) => <option key={i} value={p.name}>{p.designation ? `${p.designation} · ` : ""}{p.phone || p.email || ""}</option>)}
                      </datalist>
                    </ECompactField>
                    <ECompactField label="Client POC (Secondary)">
                      <input list="estimate-client-pocs" value={form.agent_secondary || ""} onChange={e => set("agent_secondary", e.target.value)}
                        placeholder={pocs.length ? "Search POC…" : "Pick a customer first"}
                        disabled={!sel} data-testid="est-client-poc-secondary" className={eCompact} />
                    </ECompactField>
                  </>
                );
              })()}
              <ECompactField label="Payment Terms"><input value={form.payment_terms} onChange={e => set("payment_terms", e.target.value)} placeholder="e.g. 30 Days" className={eCompact} /></ECompactField>
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Items — pick from Item Library or type custom</h3>
              <button type="button" onClick={addLine} className="text-xs font-bold uppercase tracking-wider text-[#F97316] hover:underline flex items-center gap-1"><Plus size={12} /> Add Row</button>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filter items by Group:</label>
              <select data-testid="est-group-filter" value={groupFilter} onChange={e => setGroupFilter(e.target.value)}
                      className="border border-slate-300 px-3 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none">
                <option value="">All Groups ({items.length} items)</option>
                {groups.map(g => {
                  const count = items.filter(it => _itemInGroup(it, g)).length;
                  return <option key={g.id} value={g.id}>{g.name} ({count})</option>;
                })}
              </select>
              {groupFilter && (
                <button type="button" onClick={() => setGroupFilter("")} className="text-[10px] text-slate-500 hover:text-[#F97316] underline">clear</button>
              )}
            </div>
            <div className="border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm" style={{ minWidth: 1300 }}>
                <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left py-2 px-2 w-44">Item</th>
                    <th className="text-left py-2 px-2 w-24">Item No.</th>
                    <th className="text-left py-2 px-2 w-24">Item Type</th>
                    <th className="text-left py-2 px-2">Description</th>
                    <th className="text-right py-2 px-2 w-20">Qty</th>
                    <th className="text-left py-2 px-2 w-16">Unit</th>
                    <th className="text-right py-2 px-2 w-24">Unit Amt</th>
                    <th className="text-right py-2 px-2 w-16">Mgmt %</th>
                    <th className="text-right py-2 px-2 w-20">Mgmt Amt</th>
                    <th className="text-right py-2 px-2 w-24">Total Unit Amt</th>
                    <th className="text-right py-2 px-2 w-14">GST%</th>
                    <th className="text-right py-2 px-2 w-28">Amount</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.line_items.map((l, i) => {
                    const M = EST_TYPE_PILL[l.item_type] || {};
                    // Display the pre-escalation rate. For NEW estimates (post-2026-06-16
                    // fix) `base_rate` is persisted explicitly. For LEGACY estimates
                    // (created before the fix) `base_rate` is null in the DB — we
                    // reconstruct it on the fly. `management_fee_amount` is stored
                    // **per-unit**, so base_rate = rate − mgmt (NOT mgmt/qty).
                    const baseRate = (() => {
                      if (l.base_rate !== null && l.base_rate !== undefined && l.base_rate !== "") {
                        return Number(l.base_rate);
                      }
                      const r = Number(l.rate || 0);
                      const mgmt = Number(l.management_fee_amount || 0);
                      return mgmt > 0 ? (r - mgmt) : r;
                    })();
                    const feePct = l.management_fee_type === "percent" ? Number(l.management_fee_value || 0) : 0;
                    const feeAmt = Number(l.management_fee_amount || 0);
                    const expanded = !!l._expanded;
                    return (
                    <React.Fragment key={i}>
                    <tr key={i} className="border-b border-slate-100">
                      <td className="p-1">
                        <input
                          data-testid={`est-line-item-${i}`}
                          type="text"
                          list={`est-item-list-${dlUid}-${i}`}
                          value={pickerLabelFor(l)}
                          placeholder="Search item…"
                          onChange={e => {
                            const v = e.target.value;
                            const lines = [...form.line_items];
                            lines[i] = { ...lines[i], _picker_label: v };
                            setForm({ ...form, line_items: lines });
                            const match = filteredItems.find(it => `${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}` === v);
                            if (match) pickItem(i, match.id);
                            else if (!v) pickItem(i, "");
                          }}
                          className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none bg-white text-xs"
                        />
                        <datalist id={`est-item-list-${dlUid}-${i}`}>
                          {filteredItems.map(it => (
                            <option key={it.id} value={`${it.item_code ? `${it.item_code} · ` : ""}${it.name}${it.item_type ? ` · ${it.item_type}` : ""}`} />
                          ))}
                        </datalist>
                      </td>
                      <td className="p-1"><input value={l.item_code || ""} onChange={e => setLine(i, "item_code", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1">
                        {l.item_type ? (
                          <span className={`inline-block text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 ${M.color || "bg-slate-100 text-slate-700"}`}>{l.item_type}</span>
                        ) : <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="p-1"><input required value={l.description} onChange={e => setLine(i, "description", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none" /></td>
                      <td className="p-1"><input type="number" step="0.01" value={l.quantity} onChange={e => setLine(i, "quantity", e.target.value)} className="w-full text-right px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none" /></td>
                      <td className="p-1"><input value={l.unit} onChange={e => setLine(i, "unit", e.target.value)} className="w-full px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1"><input data-testid={`est-base-rate-${i}`} type="number" step="0.01" value={baseRate} onChange={e => setLine(i, "base_rate", e.target.value)} className="w-full text-right px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs" /></td>
                      <td className="p-1 text-right tabular text-[11px] text-slate-600">{feePct ? `${feePct}%` : "—"}</td>
                      <td className="p-1 text-right tabular text-[11px] text-slate-600">{feeAmt ? formatINR(feeAmt) : "—"}</td>
                      <td className="p-1 text-right tabular text-xs font-bold text-[#F97316]">{formatINR(Number(l.rate || 0))}</td>
                      <td className="p-1"><input type="number" step="0.01" value={isTaxable ? l.gst_rate : 0} onChange={e => setLine(i, "gst_rate", e.target.value)} disabled={!isTaxable} title={isTaxable ? "" : "Disabled — Bill of Supply (Non GST)"} className={`w-full text-right px-1.5 py-1.5 border border-transparent focus:border-[#F97316] focus:outline-none text-xs ${!isTaxable ? "bg-slate-100 text-slate-400 cursor-not-allowed" : ""}`} /></td>
                      <td className="p-1 text-right tabular pr-2 font-bold text-xs">{formatINR(l.amount)}</td>
                      <td className="p-1 text-center">
                        <div className="flex items-center justify-center gap-1">
                          {l.item_id && (
                            <button
                              type="button"
                              data-testid={`est-toggle-brand-${i}`}
                              onClick={() => setLine(i, "_expanded", !expanded)}
                              title="Edit Brand / Warranty for this line only"
                              className={`p-1 ${expanded ? "text-[#F97316]" : "text-slate-400 hover:text-[#F97316]"}`}>
                              {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            </button>
                          )}
                          <button type="button" onClick={() => removeLine(i)} className="text-red-500"><Trash2 size={12} /></button>
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="bg-orange-50/40 border-b border-slate-100">
                        <td colSpan={13} className="px-3 py-2">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                              Line-level overrides — these update only this estimate, not the Item master
                            </div>
                            {form.line_items.length > 1 && (
                              <button
                                type="button"
                                data-testid={`est-apply-all-${i}`}
                                onClick={() => {
                                  if (!window.confirm(`Copy Brand / Warranty / Order Number / Order Month / Client Server from this line to all ${form.line_items.length - 1} other line(s)?`)) return;
                                  const src = lines => ({
                                    brand: lines[i].brand || "",
                                    warranty: lines[i].warranty || "",
                                    order_number: lines[i].order_number || "",
                                    order_month: lines[i].order_month || "",
                                    client_server: lines[i].client_server || "",
                                  });
                                  const lines = [...form.line_items];
                                  const payload = src(lines);
                                  for (let j = 0; j < lines.length; j++) {
                                    if (j !== i) lines[j] = { ...lines[j], ...payload };
                                  }
                                  setForm({ ...form, line_items: lines });
                                  toast.success(`Copied to ${lines.length - 1} other line(s)`);
                                }}
                                className="text-[10px] font-bold uppercase tracking-wider bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-1 inline-flex items-center gap-1"
                                title="Apply these 5 values to every other line in this estimate">
                                <Check size={11} /> Apply to all lines
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Brand / Make</div>
                              <input
                                data-testid={`est-line-brand-${i}`}
                                value={l.brand || ""}
                                onChange={e => setLine(i, "brand", e.target.value)}
                                placeholder="e.g. Schneider / Havells / Dell"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Warranty</div>
                              <input
                                data-testid={`est-line-warranty-${i}`}
                                value={l.warranty || ""}
                                onChange={e => setLine(i, "warranty", e.target.value)}
                                placeholder="e.g. 2 Years On-site"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Order Number</div>
                              <input
                                data-testid={`est-line-order-number-${i}`}
                                value={l.order_number || ""}
                                onChange={e => setLine(i, "order_number", e.target.value)}
                                placeholder="Client PO no. (free text)"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Order Month</div>
                              <input
                                data-testid={`est-line-order-month-${i}`}
                                value={l.order_month || ""}
                                onChange={e => setLine(i, "order_month", e.target.value)}
                                placeholder="e.g. Mar 2026"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                            <label className="block">
                              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Client Server</div>
                              <input
                                data-testid={`est-line-client-server-${i}`}
                                value={l.client_server || ""}
                                onChange={e => setLine(i, "client_server", e.target.value)}
                                placeholder="e.g. CSpur-DC1"
                                className="w-full px-2 py-1.5 border border-slate-300 text-xs focus:border-[#F97316] focus:outline-none bg-white"
                              />
                            </label>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>);
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals + extras */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Field label="Terms & Conditions"><textarea rows={3} value={form.terms_conditions || ""} onChange={e => set("terms_conditions", e.target.value)} className={inputCls} /></Field>
              <Field label="Notes"><textarea rows={2} value={form.notes || ""} onChange={e => set("notes", e.target.value)} className={inputCls} /></Field>
              <label className="flex items-center gap-2 cursor-pointer border border-slate-200 bg-slate-50/40 px-3 py-2">
                <input type="checkbox" data-testid="est-enable-roundup" checked={!!form.enable_round_up}
                  onChange={e => set("enable_round_up", e.target.checked)} />
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                  Round Up Total to nearest ₹1
                </span>
              </label>
            </div>
            <div className="bg-slate-50 border border-slate-200 p-5 space-y-2 self-start">
              <Row label="Subtotal" value={formatINR(subtotal)} />
              {isTaxable && isIntraState && (
                <>
                  <Row label="CGST" value={formatINR(cgst)} />
                  <Row label="SGST" value={formatINR(sgst)} />
                  <Row label="Total GST" value={formatINR(cgst + sgst)} bold />
                </>
              )}
              {isTaxable && !isIntraState && (
                <Row label="IGST" value={formatINR(igst)} />
              )}
              {!isTaxable && (
                <Row label="GST" value="— Non GST —" />
              )}
              {form.enable_round_up && (
                <Row label="Round Up Adjustment"
                  value={`${roundAdj >= 0 ? "+" : ""}${formatINR(roundAdj)}`} />
              )}
              <div className="border-t border-slate-300 pt-3 mt-3">
                <Row label="Total" value={formatINR(total)} big />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button
              type="button"
              data-testid="discard-estimate-btn"
              onClick={handleDiscard}
              className="px-5 py-2.5 bg-white border border-slate-300 text-slate-700 text-sm font-bold uppercase tracking-wider hover:bg-slate-50 hover:border-rose-400 hover:text-rose-700"
            >
              Discard
            </button>
            <button data-testid="save-estimate-btn" disabled={saving || justSaved} type="submit" className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : justSaved ? "Saved ✓" : estimate ? "Save Estimate" : "Save & Send"}
            </button>
          </div>
        </form>

        {sendModal && (
          <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="est-send-modal">
            <div className="bg-white w-full max-w-lg shadow-2xl border border-slate-200 max-h-[85vh] overflow-y-auto">
              <div className="px-5 py-3.5 border-b border-slate-200 bg-[#0B1F3A]">
                <div className="text-white font-bold text-sm uppercase tracking-wider">Confirm Email Recipients</div>
                <div className="text-slate-300 text-[11px] mt-0.5">Remove anyone who shouldn't receive this estimate, then confirm.</div>
              </div>
              <div className="p-5 space-y-4">
                {!sendModal.email_enabled && (
                  <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2" data-testid="est-send-email-disabled-note">
                    Email module is currently disabled in Email Configuration — the estimate will be saved but the email will be skipped.
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">TO Recipients</div>
                  {sendModal.to.length === 0 && (
                    <div className="text-[12px] text-slate-400 italic" data-testid="est-send-no-to">
                      No POC / shipping address selected on the voucher — no TO recipients.
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {sendModal.to.map((r, i) => (
                      <div key={i} data-testid={`est-send-to-row-${i}`}
                        className={`flex items-center justify-between gap-2 border px-3 py-2 ${(r.removed || r.movedToCc) ? "opacity-40 border-slate-200 bg-slate-50" : "border-slate-200"}`}>
                        <div className="min-w-0">
                          <div className="text-[12px] font-bold text-slate-800 truncate">
                            {r.name || "—"} <span className="font-normal text-slate-400 text-[10px] uppercase">· {r.label}</span>
                            {r.movedToCc && <span className="ml-1.5 text-[9px] font-bold uppercase text-sky-600 bg-sky-50 border border-sky-200 px-1 py-0.5" data-testid={`est-send-movedcc-badge-${i}`}>Moved to CC</span>}
                          </div>
                          <div className={`text-[11px] font-mono truncate ${r.email ? "text-slate-600" : "text-rose-500 italic"}`}>
                            {r.email || (r.duplicate ? "duplicate email — already included above" : "no email on file")}
                          </div>
                        </div>
                        {r.email && (
                          r.movedToCc ? (
                            <button type="button" data-testid={`est-send-moveback-to-${i}`}
                              onClick={() => setSendModal(m => ({
                                ...m,
                                to: m.to.map((x, j) => j === i ? { ...x, movedToCc: false } : x),
                                cc: m.cc.filter(c => c.fromToIndex !== i),
                              }))}
                              className="text-[10px] font-bold uppercase text-emerald-600 hover:underline shrink-0 whitespace-nowrap">Move back to To</button>
                          ) : r.removed ? (
                            <button type="button" data-testid={`est-send-restore-to-${i}`}
                              onClick={() => setSendModal(m => ({ ...m, to: m.to.map((x, j) => j === i ? { ...x, removed: false } : x) }))}
                              className="text-[10px] font-bold uppercase text-emerald-600 hover:underline shrink-0">Restore</button>
                          ) : (
                            <div className="flex items-center gap-2.5 shrink-0">
                              <button type="button" data-testid={`est-send-movecc-to-${i}`}
                                onClick={() => setSendModal(m => ({
                                  ...m,
                                  to: m.to.map((x, j) => j === i ? { ...x, movedToCc: true } : x),
                                  cc: [...m.cc, { email: r.email, removed: false, fromToIndex: i }],
                                }))}
                                className="text-[10px] font-bold uppercase text-sky-600 hover:underline whitespace-nowrap">→ CC</button>
                              <button type="button" data-testid={`est-send-remove-to-${i}`}
                                onClick={() => setSendModal(m => ({ ...m, to: m.to.map((x, j) => j === i ? { ...x, removed: true } : x) }))}
                                className="text-[10px] font-bold uppercase text-rose-600 hover:underline">Remove</button>
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">CC Recipients</div>
                  {sendModal.cc.length === 0 && <div className="text-[12px] text-slate-400 italic">No CC configured.</div>}
                  <div className="flex flex-wrap gap-1.5">
                    {sendModal.cc.map((c, i) => (
                      <span key={i} data-testid={`est-send-cc-${i}`}
                        className={`inline-flex items-center gap-1.5 border px-2 py-1 text-[11px] font-mono ${c.removed ? "opacity-40 line-through border-slate-200" : "border-slate-300"}`}>
                        {c.email}
                        {c.removed ? (
                          <button type="button" data-testid={`est-send-restore-cc-${i}`}
                            onClick={() => setSendModal(m => ({ ...m, cc: m.cc.map((x, j) => j === i ? { ...x, removed: false } : x) }))}
                            className="text-emerald-600 font-bold">↺</button>
                        ) : (
                          <button type="button" data-testid={`est-send-remove-cc-${i}`}
                            onClick={() => setSendModal(m => ({ ...m, cc: m.cc.map((x, j) => j === i ? { ...x, removed: true } : x) }))}
                            className="text-rose-600 font-bold">×</button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="px-5 py-3.5 border-t border-slate-200 flex flex-wrap justify-end gap-2">
                <button type="button" data-testid="est-send-cancel-btn" onClick={() => setSendModal(null)}
                  className="px-4 py-2 border border-slate-300 text-xs font-bold uppercase tracking-wider">Cancel</button>
                <button type="button" data-testid="est-save-noemail-btn" onClick={saveWithoutEmail} disabled={saving}
                  className="px-4 py-2 border border-slate-400 text-slate-700 text-xs font-bold uppercase tracking-wider hover:bg-slate-50 disabled:opacity-50">
                  Save without Email
                </button>
                <button type="button" data-testid="est-send-confirm-btn" onClick={confirmSaveAndSend} disabled={saving}
                  className="px-4 py-2 bg-[#F97316] hover:bg-[#EA580C] text-white text-xs font-bold uppercase tracking-wider disabled:opacity-50">
                  {sendModal.to.some(r => r.email && !r.removed && !r.movedToCc) ? "Save & Send Email" : "Save (no recipients)"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, bold, big }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-600 uppercase tracking-wider text-[11px]">{label}</span>
      <span className={`tabular ${bold ? "font-bold" : ""} ${big ? "text-xl font-display font-black text-[#F97316]" : "text-slate-900"}`}>{value}</span>
    </div>
  );
}


const eCompact = "w-full px-2 py-1 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none bg-white";

function ECompactField({ label, required, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 py-0.5">
      <label className="text-[11px] text-slate-600 sm:w-28 sm:shrink-0 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
